import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "../lib/router";
import { useSession } from "../lib/session";
import { Button } from "./ui";

export interface Crumb {
  label: string;
  to?: string;
}

export function Mark({ size = 30 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <rect width="32" height="32" rx="8" fill="currentColor" opacity="0.18" />
      <path d="M7 13 16 7l9 6v2H7zM9 17h3v7H9zm5.5 0h3v7h-3zM20 17h3v7h-3zM7 25h18v2H7z" fill="currentColor" />
    </svg>
  );
}

const icons = {
  overview: <path d="M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z" />,
  developers: <><circle cx="9.5" cy="8" r="3.5" /><path d="M3 20v-1a5 5 0 0 1 5-5h3a5 5 0 0 1 5 5v1M16 4.6a3.5 3.5 0 0 1 0 6.8M21 20v-1a5 5 0 0 0-3-4.6" /></>,
  games: <><path d="M7 7h10a5 5 0 0 1 5 5v0a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5v0a5 5 0 0 1 5-5z" /><path d="M6.5 12h4M8.5 10v4M15.5 13h.01M18 11h.01" /></>,
  audit: <path d="M9 6h11M9 12h11M9 18h11M4.5 6h.01M4.5 12h.01M4.5 18h.01" />,
  integrity: <path d="M12 3l8 3v6c0 4.5-3.2 7.9-8 9-4.8-1.1-8-4.5-8-9V6zM9 12l2 2 4-4" />,
  credentials: <><path d="M3 6h18v12H3z" /><path d="M7 10.5h5M7 14h7M17 10.5h.01" /></>,
  keys: <><circle cx="8" cy="15" r="4" /><path d="M11 12l9-9M16.5 6.5l3 3M14 9l2 2" /></>,
  banks: <path d="M3 10l9-6 9 6M5 10v8M9.7 10v8M14.3 10v8M19 10v8M3 20h18" />,
  currencies: <><circle cx="12" cy="12" r="9" /><path d="M15 9.5c-.5-1-1.6-1.5-3-1.5-1.7 0-3 .8-3 2s1.2 1.7 3 2 3 .8 3 2-1.3 2-3 2c-1.4 0-2.5-.5-3-1.5M12 6v2M12 16v2" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  fees: <><path d="M20 12 12 20l-8-8V4h8z" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M10 15l5-5" /></>,
  sandbox: <path d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 1.8 3h10.4a2 2 0 0 0 1.8-3l-5-9V3M7.5 15h9" />,
  cards: <><rect x="2.5" y="5" width="19" height="14" rx="2" /><path d="M2.5 10h19M6 15h4" /></>,
  savings: <><path d="M4 18l5-5 4 4 7-8" /><path d="M15 9h5v5" /></>,
  loans: <><path d="M3 15h4l4.5 2.5a2 2 0 0 0 1.5.2L20 16" /><path d="M7 15l3.5-3H14a1.5 1.5 0 0 1 0 3h-3" /><circle cx="16" cy="6" r="3" /></>,
  live: <><circle cx="12" cy="12" r="2.5" /><path d="M7.8 7.8a6 6 0 0 0 0 8.4M16.2 16.2a6 6 0 0 0 0-8.4M5 5a10 10 0 0 0 0 14M19 19a10 10 0 0 0 0-14" /></>,
  webhooks: <><path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2" /><path d="m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06" /><path d="m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8" /></>,
  activity: <path d="M3 12h4l3-8 4 16 3-8h4" />,
  settings: <path d="M4 7h10M18 7h2M4 17h4M12 17h8M14 4v6M8 14v6" />,
  signout: <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 16l4-4-4-4M14 12H4" />,
  menu: <path d="M4 6h16M4 12h16M4 18h16" />,
};

function Icon({ name }: { name: keyof typeof icons }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {icons[name]}
    </svg>
  );
}

type NavItem = { to: string; label: string; icon: keyof typeof icons; exact?: boolean };

const adminNav: NavItem[] = [
  { to: "/admin", label: "Overview", icon: "overview", exact: true },
  { to: "/admin/developers", label: "Developers", icon: "developers" },
  { to: "/admin/games", label: "Games", icon: "games" },
  { to: "/admin/audit", label: "Audit log", icon: "audit" },
  { to: "/admin/integrity", label: "Ledger health", icon: "integrity" },
];

function gameNav(gameId: string): NavItem[] {
  const b = `/dev/games/${gameId}`;
  return [
    { to: b, label: "Overview", icon: "overview", exact: true },
    { to: `${b}/live`, label: "Live balances", icon: "live" },
    { to: `${b}/keys`, label: "API keys", icon: "keys" },
    { to: `${b}/banks`, label: "Banks & limits", icon: "banks" },
    { to: `${b}/fees`, label: "Fees & charges", icon: "fees" },
    { to: `${b}/cards`, label: "Cards", icon: "cards" },
    { to: `${b}/savings`, label: "Savings", icon: "savings" },
    { to: `${b}/loans`, label: "Loans", icon: "loans" },
    { to: `${b}/currencies`, label: "Currencies", icon: "currencies" },
    { to: `${b}/clock`, label: "Game clock", icon: "clock" },
    { to: `${b}/webhooks`, label: "Webhooks", icon: "webhooks" },
    { to: `${b}/sandbox`, label: "Sandbox playground", icon: "sandbox" },
    { to: `${b}/activity`, label: "Activity", icon: "activity" },
    { to: `${b}/settings`, label: "Settings", icon: "settings" },
  ];
}

function NavLink({ item }: { item: NavItem }) {
  return (
    <Link to={item.to} exact={item.exact}>
      <Icon name={item.icon} />
      {item.label}
    </Link>
  );
}

function initials(name?: string) {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase() || "?";
}

export function Shell({ crumbs, children, gameId, gameName }: { crumbs: Crumb[]; children: ReactNode; gameId?: string; gameName?: string }) {
  const { session, signOut } = useSession();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [pathname]);
  const admin = session?.role === "admin";

  return (
    <div className="shell" data-role={admin ? "admin" : "developer"}>
      <aside className="rail" data-open={open} aria-label="Main navigation">
        <div className="brand">
          <Mark />
          <div>
            <strong>Game Banking</strong>
            <span>{admin ? "Super admin" : "Developer sandbox"}</span>
          </div>
        </div>
        <nav>
          {admin ? (
            adminNav.map((n) => <NavLink key={n.to} item={n} />)
          ) : (
            <>
              <NavLink item={{ to: "/dev", label: "Overview", icon: "overview", exact: true }} />
              <NavLink item={{ to: "/dev/games", label: "Your games", icon: "games", exact: true }} />
              <NavLink item={{ to: "/dev/account", label: "Account", icon: "credentials" }} />
              {gameId && (
                <>
                  <div className="rail-section">{gameName ?? "This game"}</div>
                  {gameNav(gameId).map((n) => <NavLink key={n.to} item={n} />)}
                </>
              )}
            </>
          )}
        </nav>
        <div className="rail-foot">
          <span className="avatar" aria-hidden="true">{initials(session?.name)}</span>
          <div className="who">
            <strong title={session?.name}>{session?.name}</strong>
            <span>{admin ? "Platform admin" : "Developer"}</span>
          </div>
          <button type="button" className="icon-btn" onClick={() => signOut()} aria-label="Sign out" title="Sign out">
            <Icon name="signout" />
          </button>
        </div>
      </aside>
      <div className="scrim" data-open={open} onClick={() => setOpen(false)} aria-hidden="true" />
      <div className="main">
        <header className="topbar">
          <Button className="menu-btn" size="small" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label="Menu">
            <Icon name="menu" />
          </Button>
          <nav className="crumbs" aria-label="Breadcrumb">
            {crumbs.map((c, i) => (
              <span key={i} className="row" style={{ gap: 8 }}>
                {i > 0 && <span className="sep">/</span>}
                {c.to ? <Link to={c.to}>{c.label}</Link> : <span aria-current="page">{c.label}</span>}
              </span>
            ))}
          </nav>
          <span className="spacer" />
          {admin && <span className="badge role plain">Acting as platform admin</span>}
        </header>
        <main className="content" id="main">
          {children}
        </main>
      </div>
    </div>
  );
}
